/*
 *  -----------------------------------------------------------------------
 *    <copyright file="signatories-editable-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { Signatory } from '@interfaces/report-templates.interface';
import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
// eslint-disable-next-line max-len
import { StudyOverviewTableConfigurationColumnConfigurations } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-study-site-overview-config/edit-study-site-overview-config.interface';
import EditableGrid from '@components/core/editable-grid';
import { EditableGridCellEditor } from '@components/core/editable-grid/editable-grid.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import Loading from '@components/core/loading';
import Error from '@components/error/error';
import { formSignatoriesList } from '@components/shared/generate-report-dialog/generate-report-dialog.helper';
import { prepareSignatoriesData } from '@components/shared/manage-signatories-grid/manage-signatories-grid.helper';
import ManageSignatoryDialog from '@components/shared/manage-signatories-grid/manage-signatory-dialog';
import {
    SignatoriesEditableGridProps,
    SignatoryGridItem,
} from '@components/shared/signatories-editable-grid/signatories-editable-grid.interface';
import { useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { useGetSignatoriesList, useMutationSignatory } from '@apis/bff-api/report-templates';

const SignatoriesEditableGrid = ({
    signatories,
    setSignatories,
    signatoriesErrors,
    setSignatoriesErrors,
    studyId,
    module,
    showAddNewSignatory,
}: SignatoriesEditableGridProps) => {
    const { t } = useTranslation();

    const [addNewIdx, setAddNewIdx] = useState<number | null>(null);

    const {
        data: signatoriesData,
        mutate: mutateSignatoriesList,
        isLoading,
        error: signatoriesError,
    } = useGetSignatoriesList(studyId, module);
    const { trigger: putSignatory, isMutating: isMutatingPutSignatory } = useMutationSignatory();

    const signatoriesList = formSignatoriesList(orderBy(signatoriesData ?? [], [{ field: 'lastName', dir: 'desc' }]));

    useEffect(() => {
        setSignatories(
            orderBy(
                signatories.map((signatory, index) => ({
                    ...signatory,
                    inEdit: true,
                    index,
                    addNew: () => {
                        setAddNewIdx(index);
                        showAddNewSignatory.setTrue();
                    },
                })),
                [{ field: 'order', dir: 'asc' }],
            ),
        );
    }, []);

    const createSignatory = (data: Signatory) => {
        const createData = prepareSignatoriesData(data, module, studyId);

        putSignatory(createData)
            .then((res) => {
                const newSignatoryId = res[0];
                NotificationService.showNotification(t('cm:signatorySuccessfulCreate'));
                showAddNewSignatory.setFalse();

                mutateSignatoriesList().then(() => {
                    setSignatories(
                        signatories.map((signatory) => {
                            if (signatory.index === addNewIdx) {
                                return {
                                    ...signatory,
                                    signatory: newSignatoryId,
                                };
                            }
                            return { ...signatory };
                        }),
                    );
                });
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const getAvailableSignatories = (_field: string, _order: number | null | undefined, value: string) =>
        signatoriesList.filter(
            (signatory) =>
                signatories.findIndex((selectedSignatory) => selectedSignatory.signatory === signatory.id) === -1 ||
                signatory.id === value,
        );

    const addNew = () => {
        const index = signatories.length + 1;
        const newDataItem: SignatoryGridItem = {
            inEdit: true,
            signatory: null,
            order: index,
            index,
            addNew: () => {
                setAddNewIdx(index);
                showAddNewSignatory.setTrue();
            },
        };

        const newSignatories = signatories.map((signatory) => ({ ...signatory }));
        newSignatories.unshift(newDataItem);

        setSignatories(newSignatories);
    };

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: 'reports:addSignatory',
                    onClick: addNew,
                    icon: ['fal', 'add'],
                    enabled: true,
                },
            ],
        },
    ];

    if (signatoriesError) return <Error error={signatoriesError} />;

    if (isLoading) return <Loading float />;

    return (
        <>
            {showAddNewSignatory.value && (
                <ManageSignatoryDialog
                    onClose={() => showAddNewSignatory.setFalse()}
                    onCreate={createSignatory}
                    isLoading={isMutatingPutSignatory}
                />
            )}
            <EditableGrid
                customKey="columns-reorder-grid"
                className="signatories-grid"
                items={signatories}
                columns={[
                    {
                        label: t('cm:signatory'),
                        field: 'signatory',
                        id: 'cm-report-signatory-data',
                        cellEditor: EditableGridCellEditor.SELECT,
                        required: true,
                        disabled: false,
                        show: true,
                        selectValues: getAvailableSignatories,
                        validateFn: (stateVal: StudyOverviewTableConfigurationColumnConfigurations) => {
                            setSignatoriesErrors((e) => ({
                                ...e,
                                [`signatory-${stateVal.index}`]: '',
                            }));
                        },
                    },
                ]}
                title={t('cm:signatories')}
                extraMenuItems={extraMenuItems}
                setState={setSignatories}
                errors={signatoriesErrors}
                setErrors={setSignatoriesErrors}
                splitItemText={false}
                lineHeight="19px"
            />
        </>
    );
};

export default SignatoriesEditableGrid;
